import { createMemo } from 'solid-js';
import { useProjectRegion } from '@trimble/tc-issue-management-providers';
import type { ProjectQueries } from './useProjectQueries';

interface ErrorInfo {
  title: string;
  message: string;
  icon: string;
}

/**
 * Maps query keys to user-friendly error messages
 */
function getErrorMessage(queryKey: string): ErrorInfo {
  const errorMap: Record<string, ErrorInfo> = {
    userQuery: {
      title: 'Authentication Error',
      message: 'Failed to authenticate user. Please check your access token.',
      icon: 'icon-font tc-icon-warning',
    },
    accessPolicyQuery: {
      title: 'Access Policy Error',
      message: 'Failed to load access policy. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
    projectUsersQuery: {
      title: 'Failed to load project users',
      message:
        'There was an error loading the project users. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
    projectGroupsQuery: {
      title: 'Failed to load project groups',
      message:
        'There was an error loading the project groups. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
    configurationQuery: {
      title: 'Failed to load configuration',
      message:
        'There was an error loading the project configuration. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
    issuesQuery: {
      title: 'Failed to load issues',
      message: 'There was an error loading the issues. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
    tagsQuery: {
      title: 'Failed to load tags',
      message: 'There was an error loading the project tags. Please try again.',
      icon: 'icon-font tc-icon-warning',
    },
  };

  return (
    errorMap[queryKey] || {
      title: 'Error',
      message: 'An unexpected error occurred. Please try again.',
      icon: 'icon-font tc-icon-warning',
    }
  );
}

/**
 * Custom hook for unified loading and error state management
 */
export function useQueryStates(queries: ProjectQueries): {
  isLoading(): boolean;
  hasError(): boolean;
  errorInfo(): ErrorInfo | null;
} {
  const projectRegion = useProjectRegion();

  // Check if data is loading
  const isLoading = createMemo(() => {
    // Check if region is loading first - everything else depends on this
    if (projectRegion.isLoading) {
      return true;
    }

    // Check each query's loading state
    if (queries.userQuery.isLoading) return true;
    if (queries.accessPolicyQuery.isLoading) return true;
    if (queries.projectUsersQuery.isLoading) return true;
    if (queries.projectGroupsQuery.isLoading) return true;
    if (queries.configurationQuery.isLoading) return true;

    // Handle the memoized issues query
    const issuesQueryResult = queries.issuesQuery();
    if (issuesQueryResult?.isLoading) return true;

    // Check tags query
    return queries.tagsQuery.isLoading ?? false;
  });

  // Check if there are any errors
  const hasError = createMemo(() => {
    if (queries.userQuery.isError) return true;
    if (queries.accessPolicyQuery.isError) return true;
    if (queries.projectUsersQuery.isError) return true;
    if (queries.projectGroupsQuery.isError) return true;
    if (queries.configurationQuery.isError) return true;

    // Handle the memoized issues query
    const issuesQueryResult = queries.issuesQuery();
    if (issuesQueryResult?.isError) return true;

    return queries.tagsQuery.isError ?? false;
  });

  // Get error information for display
  const errorInfo = createMemo((): ErrorInfo | null => {
    // Check each query for errors and return the first one found
    const queryChecks = [
      { key: 'userQuery', query: queries.userQuery },
      { key: 'accessPolicyQuery', query: queries.accessPolicyQuery },
      { key: 'projectUsersQuery', query: queries.projectUsersQuery },
      { key: 'projectGroupsQuery', query: queries.projectGroupsQuery },
      { key: 'configurationQuery', query: queries.configurationQuery },
      { key: 'tagsQuery', query: queries.tagsQuery },
    ];

    for (const { key, query } of queryChecks) {
      if (query.isError) {
        return getErrorMessage(key);
      }
    }

    // Check the memoized issues query
    const issuesQueryResult = queries.issuesQuery();
    if (issuesQueryResult?.isError) {
      return getErrorMessage('issuesQuery');
    }

    return null;
  });

  return {
    isLoading,
    hasError,
    errorInfo,
  };
}
